

























































































































import { i18n } from '@tt4b/ttam-i18n';
import { AdgroupFormData, AttributionStatisticType, AttributionWindowType, CreateMode } from '@tt4b/ttam-enums';
import { computed, defineComponent, onMounted, PropType, ref, toRefs } from '@vue/composition-api';
import QuestionTooltip from '@/components/common/question-tooltip/index.vue';
import AttributionWindowSetting from '@/components/adgroup/attribution-settings/attribution-window/index.vue';
import AttributionStatisticTypeSetting from '@/components/adgroup/attribution-settings/statistic-type/index.vue';
import { useAdgroupMode } from '@tt4b/ttam-hooks';
import { trackEvent } from '@tt4b/ttam-utils';

export default defineComponent({
  name: 'TtamAttributionSettingsCompose',

  components: {
    QuestionTooltip,
    AttributionWindowSetting,
    AttributionStatisticTypeSetting,
  },

  props: {
    createMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.CREATE,
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      required: true,
    },
    isSpcMode: {
      type: Boolean,
      default: false,
    },

    isAttributionStatisticTypeVisible: {
      type: Boolean,
      default: false,
    },
    isAttributionWindowSettingVisible: {
      type: Boolean,
      default: false,
    },
    isStatisticTypeRecommendTipVisible: {
      type: Boolean,
      default: false,
    },
    isSupportEngagedView: {
      type: Boolean,
      default: false,
    },
    clickList: {
      type: Array,
    },
    viewList: {
      type: Array,
    },
    engagedViewList: {
      type: Array,
    },
  },

  setup(props, { emit }) {
    const isReadonlyMode = ref(true);

    const { createMode, adgroupData, isSupportEngagedView } = toRefs(props);
    const { isEdit } = useAdgroupMode({ createMode });

    const attributionWindowView = computed(() => adgroupData.value.attributionWindowView);
    const attributionWindowClick = computed(() => adgroupData.value.attributionWindowClick);
    const attributionStatisticType = computed(() => adgroupData.value.attributionStatisticType);
    const attributionWindowEngagedView = computed(() => {
      // 编辑场景下，有CTA、无EVTA，使用CTA回显
      if (isEdit.value && adgroupData.value.attributionFlag && !adgroupData.value.attributionWindowEngagedView) {
        return adgroupData.value.attributionWindowClick;
      }
      return adgroupData.value.attributionWindowEngagedView;
    });

    const titleTooltip = computed(() => {
      if (
        isSupportEngagedView.value &&
        props.isAttributionWindowSettingVisible &&
        props.isAttributionStatisticTypeVisible
      ) {
        return i18n.t('adgroup_attribution_settings_module_table_title_tooltip');
      }
      if (isSupportEngagedView.value && props.isAttributionWindowSettingVisible) {
        return i18n.t('adgroup_attribution_settings_module_only_window_new_tooltip');
      }
      if (props.isAttributionWindowSettingVisible && props.isAttributionStatisticTypeVisible) {
        return i18n.t('adgroup_attribution_settings_module_table_title_tooltip');
      }

      if (props.isAttributionWindowSettingVisible) {
        return i18n.t('adgroup_attribution_settings_module_only_window_tooltip');
      }

      if (props.isAttributionStatisticTypeVisible) {
        return i18n.t('adgroup_attribution_settings_module_only_statistic_type_tooltip');
      }

      return '';
    });

    const emptyValueArr = [AttributionWindowType.NONE, AttributionWindowType.UNSET, undefined];

    const getLabel = (value: number, labelKey: string) =>
      emptyValueArr.includes(value) ? '' : i18n.t(labelKey, { num: value });

    const getAttributionWindowLabelWithEVTA = () => {
      const options = [
        {
          value: attributionWindowClick.value,
          labelKey: 'adgroup_attribution_window_click_option_label_plural',
        },
        {
          value: attributionWindowEngagedView.value,
          labelKey: 'adgroup_attribution_window_engaged_option_label_plural',
        },
        {
          value: attributionWindowView.value,
          labelKey: 'adgroup_attribution_window_view_option_label_plural',
        },
      ];

      // 过滤空值
      const labels = options
        .filter(opt => !emptyValueArr.includes(opt.value))
        .map(opt => getLabel(opt.value, opt.labelKey));

      if (!labels.length) {
        return '-';
      }
      // attribution window label 显示内容
      const content = [
        labels[0],
        i18n.t('adgroup_attribution_window_combine_two_options_label', { label1: labels[0], label2: labels[1] }),
        i18n.t('adgroup_attribution_window_combine_three_options_label', {
          label1: labels[0],
          label2: labels[1],
          label3: labels[2],
        }),
      ];

      // 根据过滤空值后的数组长度，选择组合的starling key
      return labels.length <= content.length ? content[labels.length - 1] : '';
    };

    const windowLabel = computed(() => {
      if (isSupportEngagedView.value) {
        return getAttributionWindowLabelWithEVTA();
      }
      const clickLabel = getLabel(attributionWindowClick.value, 'adgroup_attribution_window_click_option_label');

      const viewLabel = getLabel(attributionWindowView.value, 'adgroup_attribution_window_view_option_label');

      if (!clickLabel && !viewLabel) {
        return '-';
      }
      if (!viewLabel) {
        return clickLabel;
      }
      if (!clickLabel) {
        return viewLabel;
      }

      return i18n.t('adgroup_attribution_window_setting_label', { click: clickLabel, view: viewLabel });
    });
    const statisticTypeLabel = computed(() => {
      if (!attributionStatisticType.value) {
        return '-';
      }

      const key = `adgroup_attribution_statistic_type_option_${
        attributionStatisticType.value === AttributionStatisticType.ONCE ? 'once' : 'every'
      }`;

      return i18n.t(key);
    });

    function onChange(obj) {
      emit('change', {
        attributionWindowView: attributionWindowView.value,
        attributionWindowClick: attributionWindowClick.value,
        attributionStatisticType: attributionStatisticType.value,
        ...obj,
      });
    }

    function changeToEditMode() {
      isReadonlyMode.value = false;
      trackEvent('ttam_adgroup_attribution_settings_edit_click');
      emit('start-edit');
    }

    function changeStatisticTypeToEvery() {
      onChange({ attributionStatisticType: AttributionStatisticType.EVERY });
    }

    onMounted(() => {
      if (props.isSpcMode) {
        changeToEditMode();
      }
    });

    return {
      isEdit,
      isReadonlyMode,
      windowLabel,
      titleTooltip,
      statisticTypeLabel,
      attributionWindowView,
      attributionWindowClick,
      attributionStatisticType,
      onChange,
      changeToEditMode,
      changeStatisticTypeToEvery,
    };
  },
});
